import { CCard, CContainer, CRow } from "@coreui/react-pro";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pagination } from "../../../core/interfaces";
import { useActionBarStore, useIdentityStore, usePaginationStore } from "../../../core/store";
import { AppliedFilters, CellLink, Divider, RoundedSquareIcon, SearchTable, CellDraftBoolean } from "../../components";
import { GridLoader } from "react-spinners";
import NoItems from "../../components/ui/no-items/NoItems";
import { BASE_ROUTE, ROUTE_NAME_ID_2, RouteName } from "../../../core/constants/route_name";
import { DataGridPro, GridColDef, GridRenderCellParams, GridRowParams, GridSortModel, GridState } from "@mui/x-data-grid-pro";
import { useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import moment from "moment";
import { MyColumnMenu } from "../../components/table/mui/MyColumnMenu";
import { LOCAL_STORAGE_KEYS } from "../../../core/constants/local_storage";
import { useGridLocalStorage } from "../../components/table/hook/useGridLocalStorage";
import { Program } from "../../../core/models/db"; // Ajusta la ruta de tu modelo
import { useProgram } from "../../../core/hooks/db/fisioteduca/useFisioteduca"; // Hook análogo a useSponsor
import CellDate from "../../components/table/cell-date/CellDate";
import { useTranslation } from "react-i18next";

const ProgramPage = () => {
    const { identity } = useIdentityStore();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const {
        gridState,
        saveGridState,
    } = useGridLocalStorage(LOCAL_STORAGE_KEYS.TABLE_PROGRAMS);

    const {
        isShowActionBar,
        reset,
        loadActionBar,
        showActionBar,
        hideActionBar,
    } = useActionBarStore((state) => state);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const INIT_PAGINATION = useMemo(
        () => ({
            page: 0,
            itemsPerPage: 10,
            filters: {},
            orderBy: null,
        }),
        []
    );

    const { pagination, setPagination, resetFilterRaws } = usePaginationStore();
    const [init, setInit] = useState(false);
    const [body, setBody] = useState<Program[]>([]);
    const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);

    const { getPrograms } = useProgram();

    // 1) Setup paginación inicial + reset de filtros
    useEffect(() => {
        setPagination({ ...INIT_PAGINATION });
        resetFilterRaws();
        setInit(true);
    }, []);

    // 2) Mostrar ActionBar cuando haya selección
    useEffect(() => {
        if (selectedRowIds.length > 0) {
            const roleAllowed = ["ROLE_ADMIN", "ROLE_DEVELOPER", "ROLE_MANAGER"];
            loadActionBar({
                delete: roleAllowed.includes(identity.role),
                table: "program",
                count: selectedRowIds.length,
                items: selectedRowIds,
            });
            setLoading(false);
            showActionBar();
        } else {
            hideActionBar();
        }
    }, [selectedRowIds]);

    // 3) Cargar la lista cuando cambien paginación / filtros
    useEffect(() => {
        if (!init) return;
        loadData(pagination);
    }, [
        init,
        pagination?.page,
        pagination?.itemsPerPage,
        JSON.stringify(pagination?.orderBy),
        JSON.stringify(pagination?.filters),
    ]);

    // 4) Si se pide reset
    useEffect(() => {
        if (reset) {
            loadData({ ...INIT_PAGINATION });
            setSelectedRowIds([]);
        }
    }, [reset]);

    // 5) Función para cargar datos (modo servidor)
    async function loadData(pag = pagination) {
        setLoading(true);
        try {
            let pagAux = { ...pag, page: pag.page + 1 };

            pagAux.filters = { ...pag.filters };

            if (!pagAux?.orderBy) {
                pagAux.orderBy = { field: "startPublicationDate", order: "desc" };
            }
            const response = await getPrograms(pagAux);
            if (response?.ok) {
                setBody(response.item.rows);
                setPagination({
                    ...pag,
                    totalItems: response.item.pagination.totalItems,
                    totalPages: response.item.pagination.totalPages,
                });
            } else {
                console.warn("Problema al devolver programs");
            }
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    }

    // 6) Columnas DataGrid
    // const columns: GridColDef[] = useMemo(() => {
    //     return [
    //         {
    //             field: "title",
    //             headerName: t("screens.program.table.title") || "Title",
    //             flex: 1,
    //             sortable: true,
    //         },
    //         {
    //             field: "isValid",
    //             headerName: t("screens.program.table.isValid") || "Published",
    //             type: "boolean",
    //             align: "center",
    //             headerAlign: "center",
    //             width: 120,
    //             sortable: true,
    //             renderCell: (params: GridRenderCellParams<Program, boolean>) => {

    //                 return <CellDraftBoolean isValid={params.value} />;
    //             },
    //         },
    //         {
    //             field: "startPublicationDate",
    //             headerName: t("screens.program.table.startPublicationDate") || "Start Pub. Date",
    //             type: "boolean",
    //             align: "center",
    //             headerAlign: "center",
    //             width: 180,
    //             sortable: true,
    //             renderCell: (params: GridRenderCellParams<Program, string>) => {
    //                 return <CellDate startDate={params.value} hasTime={true} />;
    //             },
    //         }
    //     ];
    // }, [gridState.columnWidths]);

    const columns: GridColDef[] = useMemo(() => {
        if (isMobile) {
            // Definición de columnas para dispositivos móviles
            return [
                {
                    field: "title",
                    headerName: t("screens.program.table.title") || "Title",
                    width: 150, // Ancho fijo
                    sortable: true,
                    // Opcional: ajustar el tipo de renderizado para mejor visualización en móviles

                },
                {
                    field: "isValid",
                    headerName: t("screens.program.table.isValid") || "Published",
                    type: "boolean",
                    align: "center",
                    headerAlign: "center",
                    width: 120, // Ancho fijo
                    sortable: true,
                    renderCell: (params: GridRenderCellParams<Program, boolean>) => (
                        <CellDraftBoolean isValid={params.value} />
                    ),
                },
                {
                    field: "startPublicationDate",
                    headerName: t("screens.program.table.startPublicationDate") || "Start Pub. Date",
                    width: 200, // Ancho fijo
                    sortable: true,
                
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params: GridRenderCellParams<Program, string>) => (
                        <CellDate startDate={params.value} hasTime={true} />
                    ),
                },
                // Opcional: ocultar columnas menos importantes o combinar información
            ];
        } else {
            // Definición de columnas para dispositivos de escritorio
            return [
                {
                    field: "title",
                    headerName: t("screens.program.table.title") || "Title",
                    flex: 1,
                    sortable: true,

                },
                {
                    field: "isValid",
                    headerName: t("screens.program.table.isValid") || "Published",
                    type: "boolean",
                    align: "center",
                    headerAlign: "center",
                    width: 120,
                    sortable: true,
                    renderCell: (params: GridRenderCellParams<Program, boolean>) => (
                        <CellDraftBoolean isValid={params.value} />
                    ),
                },
                {
                    field: "startPublicationDate",
                    headerName: t("screens.program.table.startPublicationDate") || "Start Pub. Date",
                    align: "center",
                    headerAlign: "center",
                    width: 180,
                    sortable: true,
                    renderCell: (params: GridRenderCellParams<Program, string>) => (
                        <CellDate startDate={params.value} hasTime={true} />
                    ),
                },
                // Añade más columnas según sea necesario
            ];
        }
    }, [isMobile, t]);

    // 7) Creamos rows con un "id" para la DataGrid
    const rows = useMemo(
        () => body.map((item) => ({ ...item, id: item.id })),
        [body]
    );

    // 8) Manejo de paginación DataGridPro
    const handlePaginationModelChange = (model: { page: number; pageSize: number }) => {
        if (model.pageSize !== pagination?.itemsPerPage) {
            setPagination({ ...pagination, page: 0, itemsPerPage: model.pageSize });
        } else if (model.page !== pagination?.page) {
            setPagination({ ...pagination, page: model.page });
        }
    };

    // 9) Manejo de sort DataGridPro
    const handleSortModelChange = (sortModel: GridSortModel) => {
        if (sortModel.length > 0) {
            const { field, sort } = sortModel[0];
            setPagination({
                ...pagination,
                orderBy: { field, order: sort },
            });
        } else {
            setPagination({
                ...pagination,
                orderBy: null,
            });
        }
    };

    // 10) Click en fila => goToUpdate
    const goToUpdate = (program: Program) => {
        const roleAllowed = ["ROLE_ADMIN", "ROLE_DEVELOPER", "ROLE_MANAGER"];
        if (identity && roleAllowed.includes(identity.role)) {
            const pagesRoutes = t("routes", { returnObjects: true });
            const newRoute = ROUTE_NAME_ID_2({ id: program.id }, pagesRoutes, RouteName.programDetail);
            navigate(`${BASE_ROUTE}${newRoute}`, { state: { id: program.id } });
        }
    };

    // Agregar nuevo
    const goToAdd = () => {
        const pagesRoutes = t("routes", { returnObjects: true });
        const newRoute = ROUTE_NAME_ID_2({}, pagesRoutes, RouteName.programAdd);
        navigate(`${BASE_ROUTE}${newRoute}`);
    };

    const initialState = useMemo(
        () => ({
            pinnedColumns: gridState.pinnedColumns,
            columns: {
                orderedFields: gridState.columnOrder,
                columnWidths: gridState.columnWidths,
            },
        }),
        [gridState.pinnedColumns, gridState.columnOrder, gridState.columnWidths]
    );

    return (
        <CContainer>
            <div className="flex">
                <div className="w-full flex justify-between items-center pt-4 pb-5">
                    <div>
                        <span className="text-2xl sm:text-4xl" style={{ fontWeight: 500 }}>
                            {t("screens.program.title") || "Programs"}
                        </span>
                    </div>
                    <div
                        onClick={goToAdd}
                        className="px-3 py-1 rounded-xl flex items-center bg-principalColor text-white hover:bg-principalColor-500 hover:cursor-pointer"
                    >
                        <span className="text-2xl mr-2">+</span> <span>Agregar</span>
                    </div>
                </div>
            </div>

            <CCard className="rounded-xl p-3">
                {/* Filtros y búsquedas */}
                {/* <CRow>
                    <SearchTable nameColumn={["title"]} label={["Nombre"]} />
                </CRow>
                <Divider marginX={-16} />
                <CRow>
                    <AppliedFilters />
                </CRow>
                <Divider marginX={-16} /> */}
                <CRow>
                    {loading && (
                        <div className="w-full h-[250px] flex justify-center items-center">
                            <GridLoader color="#3d4f65" size={15} speedMultiplier={0.7} />
                        </div>
                    )}

                    {body && body.length > 0 && pagination && pagination?.totalPages && (
                        <div style={{ visibility: loading ? "hidden" : "visible" }}>
                            <DataGridPro
                                localeText={{
                                    columnMenuHideColumn: t("tableGeneral.columnMenuHideColumn"),
                                    columnMenuUnsort: t("tableGeneral.columnMenuUnsort"),
                                    columnMenuSortAsc: t("tableGeneral.columnMenuSortAsc"),
                                    columnMenuSortDesc: t("tableGeneral.columnMenuSortDesc"),
                                    columnMenuManageColumns: t("tableGeneral.columnMenuManageColumns"),
                                    columnsManagementReset: t("tableGeneral.columnsManagementReset"),
                                    pinToLeft: t("tableGeneral.pinToLeft"),
                                    pinToRight: t("tableGeneral.pinToRight"),
                                    unpin: t("tableGeneral.unpin"),
                                }}
                                slots={{
                                    columnMenu: MyColumnMenu,
                                }}
                                initialState={initialState}
                                onStateChange={(newState: GridState) => {
                                    const nextColumnOrder = newState.columns.orderedFields;
                                    const nextPinned = newState.pinnedColumns;
                                    saveGridState({
                                        columnOrder: nextColumnOrder,
                                        pinnedColumns: nextPinned,
                                    });
                                }}
                                rows={rows}
                                columns={columns}
                                checkboxSelection
                                disableRowSelectionOnClick={false}
                                onRowClick={(params: GridRowParams, event) => {
                                    if (selectedRowIds.length === 0) {
                                        event.stopPropagation();
                                        goToUpdate(params.row as Program);
                                    }
                                }}
                                rowSelectionModel={selectedRowIds}
                                onRowSelectionModelChange={(newSelection) => {
                                    setSelectedRowIds(newSelection as string[]);
                                }}
                                pagination
                                paginationMode="server"
                                rowCount={pagination.totalItems}
                                pageSizeOptions={[5, 10, 25, 50, 100]}
                                paginationModel={{
                                    page: pagination?.page,
                                    pageSize: pagination?.itemsPerPage,
                                }}
                                onPaginationModelChange={handlePaginationModelChange}
                                sortingMode="server"
                                onSortModelChange={handleSortModelChange}
                                slotProps={{
                                    baseCheckbox: {
                                        icon: <RoundedSquareIcon checked={false} />,
                                        checkedIcon: <RoundedSquareIcon checked={true} />,
                                        indeterminateIcon: <RoundedSquareIcon indeterminate />,
                                        style: { padding: 0 },
                                    },
                                }}
                                sx={{
                                    border: 0,
                                    "& .MuiDataGrid-columnHeaders": {
                                        borderBottom: "none",
                                    },
                                    "& .MuiDataGrid-row": {
                                        borderBottom: "none",
                                    },
                                    "& .MuiDataGrid-columnSeparator": {
                                        visibility: "hidden",
                                    },
                                    "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator": {
                                        visibility: "visible",
                                    },
                                    "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
                                        display: "none",
                                    },
                                }}
                            />
                        </div>
                    )}

                    {!loading && rows.length === 0 && <NoItems />}
                </CRow>
            </CCard>
        </CContainer>
    );
};

export default ProgramPage;
